import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import shreeCementLogo from '../assets/shree_cement.jpg';
import dalmiaCementLogo from '../assets/dalmia_cement.png';
import sagarCementLogo from '../assets/sagar_cement.jpg';
import { FaBox, FaTruckMoving } from 'react-icons/fa';

const API_URL = 'https://efrzstnoeinmysynuegy.supabase.co/rest/v1/product_pricing_latest'; // Replace with actual URL
const API_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVmcnpzdG5vZWlubXlzeW51ZWd5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA2ODA3MTEsImV4cCI6MjA2NjI1NjcxMX0.p3N6MsKXx_Mlb0V0G7n4xyYxu4PXMiKLFIjwlTpQsgY'; // Replace with actual anon/public key

function roundToNearest5(num) {
  return Math.round(num / 5) * 5;
}

// Placeholder brand images (add more as needed)
const brandImages = {
  'Shree cement': shreeCementLogo,
  'Dalmia cement': dalmiaCementLogo,
  'Sagar cement': sagarCementLogo,
  // Add more brand names and their image URLs here
};
const defaultBrandImage = 'https://via.placeholder.com/80x80?text=Brand';

function groupByProduct(products) {
  const grouped = {};
  for (const prod of products) {
    if (!grouped[prod.product_name]) grouped[prod.product_name] = [];
    grouped[prod.product_name].push(prod);
  }
  return grouped;
}

function OrderModal({ open, onClose, product, brand, price, brandImg, user }) {
  const [quantity, setQuantity] = useState('');
  const [address, setAddress] = useState('');
  const [pincode, setPincode] = useState('');
  const [phone, setPhone] = useState(user?.phone || '');
  const [gstin, setGstin] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleOrder = (e) => {
    e.preventDefault();
    // Pincode validation: must be 6 digits
    if (!/^[1-9][0-9]{5}$/.test(pincode)) {
      setError('Please enter a valid 6-digit pincode.');
      return;
    }
    setError('');
    // TODO: Save to Supabase orders table
    // For now, just log
    console.log({
      user: user?.email,
      product,
      brand,
      price,
      quantity,
      address,
      pincode,
      phone,
      gstin,
    });
    setSuccess(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md relative">
        <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl" onClick={onClose}>&times;</button>
        {success ? (
          <div className="text-center py-8">
            <div className="text-2xl text-accent font-bold mb-2">Order placed!</div>
            <div className="text-gray-700 mb-4">We will contact you soon.</div>
            <button className="bg-accent text-white px-4 py-2 rounded font-semibold" onClick={onClose}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleOrder} className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <img src={brandImg} alt={brand} className="h-12 w-12 object-contain" />
              <div>
                <div className="font-semibold text-base">{brand}</div>
                <div className="text-primary text-sm">{product}</div>
              </div>
            </div>
            <div className="text-2xl font-bold text-accent">₹{price}</div>
            <label className="text-sm font-medium">Quantity (in tons)</label>
            <input
              type="number"
              min="1"
              required
              placeholder="Quantity in tons"
              className="border rounded px-3 py-2"
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
            />
            <input
              type="text"
              required
              placeholder="Delivery Address"
              className="border rounded px-3 py-2"
              value={address}
              onChange={e => setAddress(e.target.value)}
            />
            <input
              type="text"
              required
              placeholder="Delivery Pincode"
              className="border rounded px-3 py-2"
              value={pincode}
              onChange={e => setPincode(e.target.value)}
              maxLength={6}
              pattern="[1-9][0-9]{5}"
            />
            {error && <div className="text-red-500 text-sm">{error}</div>}
            <input
              type="tel"
              required
              placeholder="Contact Phone"
              className="border rounded px-3 py-2"
              value={phone}
              onChange={e => setPhone(e.target.value)}
            />
            <input
              type="text"
              placeholder="GSTIN (optional)"
              className="border rounded px-3 py-2"
              value={gstin}
              onChange={e => setGstin(e.target.value)}
            />
            <button type="submit" className="bg-accent text-white px-4 py-2 rounded font-semibold hover:bg-primary transition">Place Order</button>
          </form>
        )}
      </div>
    </div>
  );
}

const productTypeInfo = (productName) => {
  if (/opc 53 loose/i.test(productName)) {
    return { label: 'bulker', icon: <FaTruckMoving className="inline-block text-lg ml-1" title="Bulker" /> };
  }
  if (/opc 53|ppc/i.test(productName)) {
    return { label: 'bags', icon: <FaBox className="inline-block text-lg ml-1" title="Bags" /> };
  }
  return { label: '', icon: null };
};

const ProductPricing = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderModal, setOrderModal] = useState({ open: false });

  useEffect(() => {
    if (!user) return;
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(API_URL, {
          headers: {
            'apikey': API_KEY,
            'Authorization': `Bearer ${API_KEY}`,
            'Content-Type': 'application/json',
          },
        });
        if (!res.ok) throw new Error('Failed to fetch product pricing');
        const data = await res.json();
        setProducts(data);
      } catch (err) {
        setError(err.message);
        setProducts([]);
      }
      setLoading(false);
    };
    fetchProducts();
  }, [user]);

  if (!user) return <div className="text-center mt-10">You must be logged in to view product pricing.</div>;
  if (loading) return <div className="text-center mt-10">Loading...</div>;
  if (error) return <div className="text-center mt-10 text-red-500">{error}</div>;

  const grouped = groupByProduct(products);

  return (
    <div className="max-w-5xl mx-auto mt-10 p-4">
      <h2 className="text-2xl font-bold mb-2">Today's price</h2>
      <div className="mb-4 text-sm text-gray-700 font-medium flex items-center gap-2">
        <span>All prices are per 50 Kgs</span>
        <FaBox className="inline-block text-base ml-2 mr-1 text-gray-500" title="Bags" /> Bags
        <FaTruckMoving className="inline-block text-base ml-2 mr-1 text-gray-500" title="Bulker" /> Bulker
      </div>
      <div className="flex flex-col gap-8">
        {Object.entries(grouped).map(([productName, brandProds]) => {
          const { label, icon } = productTypeInfo(productName);
          return (
            <div key={productName}>
              <div className="font-bold text-lg mb-3 text-primary flex items-center gap-2">
                {productName} {label && <span className="text-xs text-gray-600 ml-2">({label}) {icon}</span>}
              </div>
              <div className="flex flex-row flex-wrap gap-4">
                {brandProds.map((prod) => {
                  let price = '';
                  if (Array.isArray(prod.latest_prices) && prod.latest_prices.length > 0) {
                    const sorted = [...prod.latest_prices].sort((a, b) => new Date(b.price_date) - new Date(a.price_date));
                    const min = sorted.reduce((min, p) => (p.unit_price < min.unit_price ? p : min), sorted[0]);
                    const adjusted = roundToNearest5(min.unit_price * 1.1);
                    price = adjusted;
                  }
                  const brandImg = brandImages[prod.brand_name] || defaultBrandImage;
                  return (
                    <div
                      key={prod.product_master_id + '-' + prod.brand_name}
                      className="bg-white rounded-xl shadow-soft p-4 flex flex-col items-center w-40 h-56 border border-gray-100"
                    >
                      <img src={brandImg} alt={prod.brand_name} className="h-16 w-16 object-contain mb-2" />
                      <div className="font-semibold text-center text-sm mb-1">{prod.brand_name}</div>
                      <div className="text-2xl font-bold text-accent mb-2">{price !== '' ? `₹${price}` : <span className="text-gray-400">No price</span>}</div>
                      <button
                        className="mt-auto bg-accent text-white px-3 py-1 rounded font-semibold hover:bg-primary transition w-full"
                        onClick={() => setOrderModal({ open: true, product: productName, brand: prod.brand_name, price, brandImg })}
                      >
                        Place Order
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {products.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            No products found.
          </div>
        )}
      </div>
      <OrderModal
        open={orderModal.open}
        onClose={() => setOrderModal({ open: false })}
        product={orderModal.product}
        brand={orderModal.brand}
        price={orderModal.price}
        brandImg={orderModal.brandImg}
        user={user}
      />
    </div>
  );
};

export default ProductPricing; 